import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import {
  nestPortfolioDataPoints,
  sortPortfolioDataPoints,
} from '@products/portfolio/portfolio.utils';
import {
  PortfolioRiskReturnAnalysisDTO,
  PortfolioDTO,
  PortfolioComponentId,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { Mapper, MapperParams } from '../type.mapper';
import { Logger } from '@utils/logger';
import { getFirstNonEmptyProp } from '../mapper.utils';

const logger = Logger.getLogger('RiskReturnAnalysisMapper');

/**
 * example fund (no chart, nested table data):
 * https://staging.ftsites.com/en-cy/products/18904/A/franklin-diversified-balanced-fund/LU1147469677#portfolio
 */
export class RiskReturnAnalysisMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioRiskReturnAnalysisDTO[] =
      portfolioDto.riskreturnanalysis;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const filteredPoints: PortfolioRiskReturnAnalysisDTO[] = data.filter(
      (point: PortfolioRiskReturnAnalysisDTO): boolean =>
        point.fperiod === '1 Month'
    );

    const calcParent = (point: PortfolioRiskReturnAnalysisDTO): string => {
      if (point.factorl2) {
        return point.factorl1;
      }
      // parent items have empty factorl2
      return '';
    };

    let dataPoints: PortfolioAllocationDataPoint[] = filteredPoints
      .sort(sortPortfolioDataPoints)
      .map(
        (
          point: PortfolioRiskReturnAnalysisDTO
        ): PortfolioAllocationDataPoint => {
          return {
            // factorl1 is parent label, factorl2 is child label (null for parent item)
            label: point.factorl2 || point.factorl1,
            parent: calcParent(point),
            riskContributionAllocation: formatPercentage(point.portavgwgt),
            performanceContributionAllocation: formatPercentage(
              point.portcontrib
            ),
          };
        }
      );

    dataPoints = nestPortfolioDataPoints(dataPoints);

    const start: string = getFirstNonEmptyProp(filteredPoints, 'startdate');
    const end: string = getFirstNonEmptyProp(filteredPoints, 'enddate');
    const allocation: PortfolioAllocation = {
      componentId: PortfolioComponentId.PORTFOLIO_RISK_AND_RETURN_ANALYSIS,
      baseLabelKey: 'portfolio-risk-and-return-analysis',
      showExposureChart: false,
      hideChart: true,
      asOfDate: getFirstNonEmptyProp(filteredPoints, 'asofdate'),
      contributionDateRange: start && end ? `${start} - ${end}` : null,
      updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
      data: dataPoints,
    };
    logger.debug('allocation', allocation);
    return [allocation];
  }
}
